import React from 'react'
import { styled } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Chip,
  Grid2
} from '@mui/material'
import clsx from 'clsx'
import PropTypes from 'prop-types'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  position: 'static',
  '&.MuiTableCell-head': {
    backgroundColor: 'transparent',
    color: theme.palette.grey[600],
    padding: '12px',
    '&:first-of-type': {
      padding: '12px 16px'
    },
    whiteSpace: 'nowrap'
  },
  '&.MuiTableCell-body': {
    color: theme.palette.grey[600],
    padding: '12px',
    '&:first-of-type': {
      padding: '12px 16px'
    },
    fontSize: 14,
    fontWeight: 500
  }
}))

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'clickable' && prop !== 'isBs'
})(({ theme, clickable, isBs }) => ({
  position: 'relative',
  border: isBs ? '2px solid red' : 'none',
  borderRadius: isBs ? '8px' : '0px',
  backgroundColor: isBs && '#ffecec',
  '&:nth-of-type(odd)': {
    backgroundColor: !isBs && theme.palette.grey[50]
  },
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: clickable ? (isBs ? '#ffd6d6' : theme.palette.grey[100]) : undefined
  }
}))

const StandardTable = ({
  tableData,
  tableStyles,
  onRowClick,
  hasBackgroudRowClick = false,
  onButtonClick,
  onChipClick,
  btnColor = 'primary',
  btnVariant = 'outlined',
  btnStyle = {},
  className,
  columnsToTotal = []
}) => {
  const computeTotals = () => {
    const totals = Array(tableData?.columns?.length).fill(0)

    tableData?.rows.forEach(rowObj => {
      const row = Array.isArray(rowObj) ? rowObj : rowObj?.row
      if (!Array.isArray(row)) return

      row.forEach((cell, index) => {
        if (columnsToTotal?.includes(index)) {
          const cellValue = parseFloat(cell?.toString()?.replace(/[^\d.-]/g, ''))
          if (!isNaN(cellValue)) {
            totals[index] += cellValue
          }
        }
      })
    })

    return totals.map(total => (total !== 0 ? `${total?.toString()?.thousandSeparator()} ₸` : ''))
  }

  const totals = computeTotals()

  return (
    <TableContainer component={Paper} sx={{ ...tableStyles }} className={clsx(className)}>
      <Table stickyHeader aria-label="customized table">
        <TableHead>
          <TableRow>
            {tableData?.columns?.map((name, index) => (
              <StyledTableCell key={`styled_name_${index}`}>{name}</StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.rows?.map((rowObj, index) => {
            const isBs = rowObj?.isBs
            const row = Array.isArray(rowObj) ? rowObj : rowObj?.row
            return (
              <StyledTableRow
                key={`styled_row_${index}`}
                clickable={!!onRowClick || undefined}
                onClick={() => !hasBackgroudRowClick && onRowClick?.(row)}
                isBs={isBs}
              >
                {row?.map((val, indx) => (
                  <StyledTableCell key={`styled_cell_${indx}`}>
                    {hasBackgroudRowClick && (
                      <Box
                        sx={{ position: 'absolute', width: '100%', height: '100%', left: 0, top: 0, opacity: 0 }}
                        onClick={() => onRowClick?.(row)}
                      />
                    )}
                    {val?.label ? (
                      <Grid2 container spacing={1} justifyContent={val?.isButton ? 'flex-end' : 'flex-start'}>
                        <Grid2>
                          {val?.isButton && (
                            <Button
                              size="small"
                              color={btnColor}
                              variant={btnVariant}
                              sx={{ ...btnStyle, ...(val?.style || {}) }}
                              onClick={() => onButtonClick?.(val?.data ? val?.data : row)}
                            >
                              {val?.label}
                            </Button>
                          )}
                          {val?.isChip && (
                            <Chip
                              label={val?.label}
                              sx={{ ...btnStyle, ...(val?.style || {}) }}
                              onClick={() => onChipClick?.(val?.data, row)}
                            />
                          )}
                        </Grid2>
                      </Grid2>
                    ) : (
                      val
                    )}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            )
          })}
          {columnsToTotal?.length > 0 && (
            <StyledTableRow>
              {tableData.columns.map((col, index) => (
                <StyledTableCell key={`styled_total_${index}`}>
                  {columnsToTotal.includes(index) ? totals[index] : ''}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

StandardTable.propTypes = {
  tableData: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired
  }).isRequired,
  tableStyles: PropTypes.object,
  onRowClick: PropTypes.func,
  hasBackgroudRowClick: PropTypes.bool,
  onButtonClick: PropTypes.func,
  onChipClick: PropTypes.func,
  btnColor: PropTypes.string,
  btnVariant: PropTypes.string,
  btnStyle: PropTypes.object,
  className: PropTypes.string,
  columnsToTotal: PropTypes.arrayOf(PropTypes.number)
}

export default StandardTable
